import { useEffect, useState } from "react";
import {
  fetchAcademyPlans,
  createAcademyPlan,
  deleteAcademyPlan,
} from "@/api/academyAuth";

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    price: "",
    duration: "",
    features: [""],
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // تحميل خطط الأكاديمية فقط
  const loadPlans = async () => {
    try {
      const academyData = await fetchAcademyPlans();
      setPlans(academyData || []);
    } catch (e) {
      console.error(e);
      setError("فشل تحميل الخطط");
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  // إضافة خطة للأكاديمية
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      await createAcademyPlan({
        title: form.title.trim(),
        description: form.description.trim(),
        price: Number(form.price),
        duration: Number(form.duration),
        features: form.features.filter((f) => f.trim() !== ""),
      });

      setForm({
        title: "",
        description: "",
        price: "",
        duration: "",
        features: [""],
      });

      setSuccess("تم إضافة الخطة بنجاح");
      loadPlans();
      
      // إخفاء النموذج بعد الإضافة الناجحة
      setTimeout(() => {
        setIsFormVisible(false);
        setSuccess("");
      }, 2000);
    } catch (e) {
      setError("فشل إضافة الخطة. يرجى التأكد من البيانات.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // حذف خطة أكاديمية
  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه الخطة؟")) return;

    try {
      await deleteAcademyPlan(id);
      setSuccess("تم حذف الخطة بنجاح");
      loadPlans();
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError("فشل حذف الخطة");
      console.error(e);
    }
  };

  // إضافة ميزة جديدة
  const addFeature = () => {
    setForm({ ...form, features: [...form.features, ""] });
  };

  // حذف ميزة
  const removeFeature = (index) => {
    const newFeatures = form.features.filter((_, i) => i !== index);
    setForm({ ...form, features: newFeatures });
  };

  // تحديث ميزة
  const updateFeature = (index, value) => {
    const newFeatures = [...form.features];
    newFeatures[index] = value;
    setForm({ ...form, features: newFeatures });
  };

  // تنسيقات CSS
  const styles = {
    container: {
      maxWidth: "1400px",
      margin: "0 auto",
      padding: "25px 20px",
      minHeight: "calc(100vh - 160px)"
    },
    header: {
      marginBottom: "30px",
      paddingBottom: "20px",
      borderBottom: "2px solid #e9ecef"
    },
    title: {
      margin: "0 0 10px 0",
      color: "#182848",
      fontSize: "28px",
      fontWeight: "700",
      display: "flex",
      alignItems: "center",
      gap: "12px"
    },
    subtitle: {
      margin: "0 0 20px 0",
      color: "#6c757d",
      fontSize: "16px"
    },
    statsCard: {
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "20px",
      boxShadow: "0 3px 10px rgba(0, 0, 0, 0.08)",
      display: "flex",
      alignItems: "center",
      gap: "15px",
      marginBottom: "30px"
    },
    statIcon: {
      width: "50px",
      height: "50px",
      borderRadius: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: "0",
      backgroundColor: "rgba(75, 108, 183, 0.1)"
    },
    statContent: {
      flex: "1"
    },
    statTitle: {
      margin: "0 0 5px 0",
      color: "#6c757d",
      fontSize: "14px",
      fontWeight: "500"
    },
    statValue: {
      margin: "0",
      color: "#182848",
      fontSize: "24px",
      fontWeight: "700"
    },
    actionBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "25px",
      flexWrap: "wrap",
      gap: "15px"
    },
    toggleButton: {
      padding: "12px 25px",
      background: "linear-gradient(90deg, #4b6cb7 0%, #182848 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontFamily: "inherit"
    },
    plansCount: {
      color: "#182848",
      fontSize: "16px",
      fontWeight: "600",
      backgroundColor: "rgba(75, 108, 183, 0.1)",
      padding: "8px 16px",
      borderRadius: "20px"
    },
    formContainer: {
      backgroundColor: "white",
      borderRadius: "15px",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
      padding: "25px",
      marginBottom: "30px",
      display: isFormVisible ? "block" : "none",
      animation: "slideDown 0.3s ease-out"
    },
    formTitle: {
      margin: "0 0 25px 0",
      color: "#182848",
      fontSize: "20px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      paddingBottom: "15px",
      borderBottom: "1px solid #e9ecef"
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "20px"
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px"
    },
    label: {
      color: "#495057",
      fontWeight: "500",
      fontSize: "15px",
      display: "flex",
      alignItems: "center",
      gap: "5px"
    },
    required: {
      color: "#dc3545"
    },
    input: {
      padding: "14px 16px",
      border: "1px solid #e1e5eb",
      borderRadius: "10px",
      fontSize: "16px",
      transition: "all 0.3s",
      backgroundColor: "#f8f9fa",
      boxSizing: "border-box",
      fontFamily: "inherit",
      width: "100%"
    },
    textarea: {
      padding: "14px 16px",
      border: "1px solid #e1e5eb",
      borderRadius: "10px",
      fontSize: "16px",
      transition: "all 0.3s",
      backgroundColor: "#f8f9fa",
      boxSizing: "border-box",
      fontFamily: "inherit",
      width: "100%",
      minHeight: "100px",
      resize: "vertical"
    },
    featuresSection: {
      backgroundColor: "#f8f9fa",
      borderRadius: "10px",
      padding: "20px",
      marginTop: "10px"
    },
    featuresTitle: {
      margin: "0 0 15px 0",
      color: "#182848",
      fontSize: "16px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "10px"
    },
    featureItem: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "10px"
    },
    featureInput: {
      flex: "1",
      padding: "12px 15px",
      border: "1px solid #e1e5eb",
      borderRadius: "8px",
      fontSize: "15px",
      backgroundColor: "white",
      transition: "all 0.3s"
    },
    removeFeatureButton: {
      padding: "8px 12px",
      backgroundColor: "rgba(220, 53, 69, 0.1)",
      color: "#dc3545",
      border: "1px solid rgba(220, 53, 69, 0.3)",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.3s",
      minWidth: "80px"
    },
    addFeatureButton: {
      padding: "10px 15px",
      backgroundColor: "rgba(40, 167, 69, 0.1)",
      color: "#28a745",
      border: "1px solid rgba(40, 167, 69, 0.3)",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginTop: "10px"
    },
    formButtons: {
      display: "flex",
      gap: "15px",
      marginTop: "20px"
    },
    submitButton: {
      padding: "14px 25px",
      background: "linear-gradient(90deg, #4b6cb7 0%, #182848 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      flex: "1",
      fontFamily: "inherit"
    },
    cancelButton: {
      padding: "14px 25px",
      backgroundColor: "#f8f9fa",
      color: "#495057",
      border: "1px solid #e1e5eb",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      flex: "1",
      fontFamily: "inherit"
    },
    buttonDisabled: {
      opacity: "0.7",
      cursor: "not-allowed"
    },
    spinner: {
      width: "20px",
      height: "20px",
      border: "2px solid rgba(255, 255, 255, 0.3)",
      borderRadius: "50%",
      borderTopColor: "white",
      animation: "spin 1s ease-in-out infinite"
    },
    successMessage: {
      backgroundColor: "#d4edda",
      border: "1px solid #c3e6cb",
      color: "#155724",
      padding: "15px",
      borderRadius: "10px",
      marginTop: "15px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "15px",
      animation: "fadeIn 0.3s ease-out"
    },
    errorMessage: {
      backgroundColor: "#f8d7da",
      border: "1px solid #f5c6cb",
      color: "#721c24",
      padding: "15px",
      borderRadius: "10px",
      marginTop: "15px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "15px",
      animation: "fadeIn 0.3s ease-out"
    },
    icon: {
      width: "20px",
      height: "20px",
      flexShrink: "0"
    },
    plansGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
      gap: "25px",
      marginTop: "10px"
    },
    planCard: {
      backgroundColor: "white",
      borderRadius: "15px",
      overflow: "hidden",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.08)",
      transition: "all 0.3s ease",
      position: "relative",
      border: "1px solid #e9ecef",
      display: "flex",
      flexDirection: "column"
    },
    planHeader: {
      padding: "25px",
      textAlign: "center",
      backgroundColor: "#f8f9fa",
      borderBottom: "1px solid #e9ecef"
    },
    planTitle: {
      margin: "0 0 10px 0",
      color: "#182848",
      fontSize: "22px",
      fontWeight: "700"
    },
    planPrice: {
      margin: "0 0 5px 0",
      color: "#4b6cb7",
      fontSize: "36px",
      fontWeight: "800",
      display: "flex",
      alignItems: "baseline",
      justifyContent: "center",
      gap: "5px"
    },
    currency: {
      fontSize: "18px",
      color: "#6c757d",
      fontWeight: "600"
    },
    planDuration: {
      margin: "0",
      color: "#6c757d",
      fontSize: "14px"
    },
    planContent: {
      padding: "25px",
      flex: "1",
      display: "flex",
      flexDirection: "column"
    },
    planDescription: {
      margin: "0 0 20px 0",
      color: "#495057",
      fontSize: "15px",
      lineHeight: "1.6"
    },
    featuresList: {
      listStyle: "none",
      margin: "0 0 20px 0",
      padding: "0",
      flex: "1"
    },
    featureListItem: {
      padding: "8px 0",
      color: "#495057",
      fontSize: "14px",
      display: "flex",
      alignItems: "flex-start",
      gap: "10px"
    },
    featureIcon: {
      color: "#28a745",
      fontSize: "14px",
      flexShrink: "0",
      marginTop: "3px"
    },
    planFooter: {
      padding: "20px 25px",
      borderTop: "1px solid #e9ecef",
      textAlign: "center"
    },
    deleteButton: {
      padding: "10px 20px",
      backgroundColor: "rgba(220, 53, 69, 0.1)",
      color: "#dc3545",
      border: "1px solid rgba(220, 53, 69, 0.3)",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
      display: "inline-block",
      minWidth: "120px"
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
      backgroundColor: "white",
      borderRadius: "15px",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
      gridColumn: "1 / -1"
    },
    emptyIcon: {
      fontSize: "64px",
      color: "#adb5bd",
      marginBottom: "20px",
      opacity: "0.5"
    },
    emptyTitle: {
      margin: "0 0 10px 0",
      color: "#495057",
      fontSize: "24px",
      fontWeight: "600"
    },
    emptyText: {
      margin: "0 0 25px 0",
      color: "#6c757d",
      fontSize: "16px"
    }
  };

  // أيقونات SVG
  const PlanIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ ...styles.icon, color: "#4b6cb7", width: "28px", height: "28px" }}>
      <path d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32l8.4-8.4z" />
      <path d="M5.25 5.25a3 3 0 00-3 3v10.5a3 3 0 003 3h10.5a3 3 0 003-3V13.5a.75.75 0 00-1.5 0v5.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5V8.25a1.5 1.5 0 011.5-1.5h5.25a.75.75 0 000-1.5H5.25z" />
    </svg>
  );

  const SuccessIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ ...styles.icon, color: "#155724" }}>
      <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
    </svg>
  );

  const ErrorIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ ...styles.icon, color: "#721c24" }}>
      <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
    </svg>
  );

  const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: "18px", height: "18px" }}>
      <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 9a.75.75 0 00-1.5 0v2.25H9a.75.75 0 000 1.5h2.25V15a.75.75 0 001.5 0v-2.25H15a.75.75 0 000-1.5h-2.25V9z" clipRule="evenodd" />
    </svg>
  );

  const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: "16px", height: "16px" }}>
      <path fillRule="evenodd" d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z" clipRule="evenodd" />
    </svg>
  );

  const MinusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: "16px", height: "16px", color: "#dc3545" }}>
      <path fillRule="evenodd" d="M4.25 12a.75.75 0 01.75-.75h14a.75.75 0 010 1.5H5a.75.75 0 01-.75-.75z" clipRule="evenodd" />
    </svg>
  );

  // تأثيرات hover
  const handleInputFocus = (e) => {
    e.target.style.outline = "none";
    e.target.style.borderColor = "#4b6cb7";
    e.target.style.boxShadow = "0 0 0 3px rgba(75, 108, 183, 0.1)";
    e.target.style.backgroundColor = "white";
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = "#e1e5eb";
    e.target.style.boxShadow = "none";
    e.target.style.backgroundColor = "#f8f9fa";
  };

  const handleButtonHover = (e, type = "primary") => {
    if (type === "primary") {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 7px 14px rgba(75, 108, 183, 0.2)";
    } else if (type === "delete") {
      e.currentTarget.style.backgroundColor = "rgba(220, 53, 69, 0.2)";
      e.currentTarget.style.transform = "translateY(-2px)";
    } else if (type === "add") {
      e.currentTarget.style.backgroundColor = "rgba(40, 167, 69, 0.2)";
      e.currentTarget.style.transform = "translateY(-1px)";
    } else if (type === "remove") {
      e.currentTarget.style.backgroundColor = "rgba(220, 53, 69, 0.2)";
      e.currentTarget.style.transform = "translateY(-1px)";
    }
  };

  const handleButtonLeave = (e, type = "primary") => {
    if (type === "primary") {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    } else if (type === "delete") {
      e.currentTarget.style.backgroundColor = "rgba(220, 53, 69, 0.1)";
      e.currentTarget.style.transform = "translateY(0)";
    } else if (type === "add") {
      e.currentTarget.style.backgroundColor = "rgba(40, 167, 69, 0.1)";
      e.currentTarget.style.transform = "translateY(0)";
    } else if (type === "remove") {
      e.currentTarget.style.backgroundColor = "rgba(220, 53, 69, 0.1)";
      e.currentTarget.style.transform = "translateY(0)";
    }
  };

  const handleCardHover = (e) => {
    e.currentTarget.style.transform = "translateY(-8px)";
    e.currentTarget.style.boxShadow = "0 15px 30px rgba(0, 0, 0, 0.15)";
    e.currentTarget.style.borderColor = "#4b6cb7";
  };

  const handleCardLeave = (e) => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "0 5px 15px rgba(0, 0, 0, 0.08)";
    e.currentTarget.style.borderColor = "#e9ecef";
  };

  return (
    <div style={styles.container}>
      {/* رأس الصفحة */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <PlanIcon />
          خطط الأكاديمية
        </h1>
        <p style={styles.subtitle}>إدارة باقات الاشتراك وعروض الأكاديمية</p>
      </div>

      {/* إحصائيات سريعة */}
      <div style={styles.statsCard}>
        <div style={styles.statIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4b6cb7" style={{ width: "24px", height: "24px" }}>
            <path d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 01-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004zM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 01-.921.42z" />
            <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v.816a3.836 3.836 0 00-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 01-.921-.421l-.879-.66a.75.75 0 00-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 001.5 0v-.81a4.124 4.124 0 001.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 00-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 00.933-1.175l-.415-.33a3.836 3.836 0 00-1.719-.755V6z" clipRule="evenodd" />
          </svg>
        </div>
        <div style={styles.statContent}>
          <h3 style={styles.statTitle}>إجمالي الخطط المتاحة</h3>
          <p style={styles.statValue}>{plans.length}</p>
        </div>
      </div>

      {/* شريط الإجراءات */}
      <div style={styles.actionBar}>
        <button 
          style={styles.toggleButton}
          onClick={() => setIsFormVisible(!isFormVisible)}
          onMouseEnter={(e) => handleButtonHover(e, "primary")}
          onMouseLeave={(e) => handleButtonLeave(e, "primary")}
        >
          <PlusIcon />
          {isFormVisible ? "إخفاء نموذج الإضافة" : "إضافة خطة جديدة"}
        </button>
        
        <div style={styles.plansCount}>
          {plans.length} خطة{plans.length !== 1 ? 'ات' : ''}
        </div>
      </div>

      {/* نموذج إضافة خطة */}
      <div style={styles.formContainer}>
        <h2 style={styles.formTitle}>
          {isFormVisible ? "إضافة خطة جديدة" : ""}
        </h2>
        
        {isFormVisible && (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                اسم الخطة
                <span style={styles.required}>*</span>
              </label>
              <input
                placeholder="مثال: الخطة الذهبية، الخطة الأساسية"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                required
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                وصف الخطة
              </label>
              <textarea
                placeholder="وصف مختصر للخطة ومميزاتها العامة"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                style={styles.textarea}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  السعر ($)
                  <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  placeholder="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  required
                  min="0"
                  step="0.01"
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  المدة (أيام)
                  <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  placeholder="30"
                  value={form.duration}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  required
                  min="1"
                  style={styles.input}
                />
              </div>
            </div>

            {/* قسم المميزات */}
            <div style={styles.featuresSection}>
              <h3 style={styles.featuresTitle}>مميزات الخطة</h3>
              
              {form.features.map((feature, index) => (
                <div key={index} style={styles.featureItem}>
                  <input
                    placeholder={`ميزة ${index + 1}`}
                    value={feature}
                    onChange={(e) => updateFeature(index, e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    style={styles.featureInput}
                  />
                  {form.features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      style={styles.removeFeatureButton}
                      onMouseEnter={(e) => handleButtonHover(e, "remove")}
                      onMouseLeave={(e) => handleButtonLeave(e, "remove")}
                    >
                      <MinusIcon />
                    </button>
                  )}
                </div>
              ))}
              
              <button
                type="button"
                onClick={addFeature}
                style={styles.addFeatureButton}
                onMouseEnter={(e) => handleButtonHover(e, "add")}
                onMouseLeave={(e) => handleButtonLeave(e, "add")}
              >
                <PlusIcon />
                إضافة ميزة جديدة
              </button>
            </div>

            <div style={styles.formButtons}>
              <button 
                type="submit" 
                style={{
                  ...styles.submitButton,
                  ...(loading ? styles.buttonDisabled : {})
                }}
                disabled={loading}
                onMouseEnter={(e) => handleButtonHover(e, "primary")}
                onMouseLeave={(e) => handleButtonLeave(e, "primary")}
              >
                {loading ? (
                  <>
                    <div style={styles.spinner}></div>
                    جاري الإضافة...
                  </>
                ) : (
                  "إضافة الخطة"
                )}
              </button>

              <button 
                type="button" 
                onClick={() => setIsFormVisible(false)}
                style={styles.cancelButton}
                onMouseEnter={(e) => handleButtonHover(e, "secondary")}
                onMouseLeave={(e) => handleButtonLeave(e, "secondary")}
              >
                إلغاء
              </button>
            </div>

            {/* رسائل النجاح والخطأ */}
            {success && (
              <div style={styles.successMessage}>
                <SuccessIcon />
                {success}
              </div>
            )}

            {error && (
              <div style={styles.errorMessage}>
                <ErrorIcon />
                {error}
              </div>
            )}
          </form>
        )}
      </div>

      {/* شبكة الخطط */}
      <div style={styles.plansGrid}>
        {plans.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>💎</div>
            <h3 style={styles.emptyTitle}>لا توجد خطط متاحة</h3>
            <p style={styles.emptyText}>
              قم بإضافة أول خطة اشتراك لتظهر هنا
            </p>
            <button 
              style={styles.toggleButton}
              onClick={() => setIsFormVisible(true)}
              onMouseEnter={(e) => handleButtonHover(e, "primary")}
              onMouseLeave={(e) => handleButtonLeave(e, "primary")}
            >
              <PlusIcon />
              إضافة أول خطة
            </button>
          </div>
        ) : (
          plans.map((plan) => (
            <div 
              key={plan.id} 
              style={styles.planCard}
              onMouseEnter={handleCardHover}
              onMouseLeave={handleCardLeave}
            >
              <div style={styles.planHeader}>
                <h3 style={styles.planTitle}>{plan.title}</h3>
                <div style={styles.planPrice}>
                  <span style={styles.currency}>$</span>
                  {plan.price}
                </div>
                <p style={styles.planDuration}>مدة {plan.duration} يوم</p>
              </div>
              
              <div style={styles.planContent}>
                {plan.description && (
                  <p style={styles.planDescription}>{plan.description}</p>
                )}
                
                {plan.features && plan.features.length > 0 && (
                  <ul style={styles.featuresList}>
                    {plan.features.map((featureObj, index) => (
                      <li key={index} style={styles.featureListItem}>
                        <span style={styles.featureIcon}>
                          <CheckIcon />
                        </span>
                        <span>{typeof featureObj === 'object' ? featureObj.feature : featureObj}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              
              <div style={styles.planFooter}>
                <button 
                  onClick={() => handleDelete(plan.id)}
                  style={styles.deleteButton}
                  onMouseEnter={(e) => handleButtonHover(e, "delete")}
                  onMouseLeave={(e) => handleButtonLeave(e, "delete")}
                >
                  حذف الخطة
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* إضافة الأنيميشن */}
      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
            max-height: 0;
          }
          to {
            opacity: 1;
            transform: translateY(0);
            max-height: 800px;
          }
        }
        
        .plan-card {
          animation: fadeIn 0.3s ease-out;
        }
        
        @media (max-width: 768px) {
          .plans-grid {
            grid-template-columns: 1fr !important;
          }
          
          .form-buttons {
            flex-direction: column !important;
          }
          
          .feature-item {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          .remove-feature-button {
            width: 100% !important;
            margin-top: 5px !important;
          }
        }
        
        @media (max-width: 480px) {
          .action-bar {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          .toggle-button, .plans-count {
            width: 100% !important;
            text-align: center !important;
          }
        }
      `}</style>
    </div>
  );
}